import { HubCoreService, MySquid, onConfigChange, onUserChange, User } from '@hub/shared'
import { useCreatorsInsights } from '@src/hooks/useCreatorsInsights'
import { useDeviceType } from '@src/hooks/useDeviceType'
import { useFetchAllProfiles } from '@src/hooks/useFetchAllProfiles'
import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import { MainMenuItem } from './menu-mobile-item/menu-mobile-item-default.component'
import './main-menu.component.scoped.scss'
import { showInsights } from '@src/utils/showInsights'
import { SqModalActivation, SqModalWelcomeCreatorsInsights } from '@squidit/react-css'

interface Props {
  open: boolean
  setOpen: (value: SetStateAction<boolean>) => void
  mode: 'mobile' | 'desktop'
}

export const MainMenu = ({ open, setOpen, mode }: Props) => {
  const { t } = useTranslation('defaultLayout')
  const [isCloseMobileMenuHovered, setIsCloseMobileMenuHovered] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [configs, setConfig] = useState<MySquid | null>(null)
  const { profiles, setProfiles, profilesLoading, fetchAllProfiles } = useFetchAllProfiles(user?.squidId)
  const [registerCompleted, setRegisterCompleted] = useState<boolean>(false)
  const hubCoreService = useMemo(() => new HubCoreService(), [])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      if (userChanged?.squidId) {
        setUser(userChanged)
      }
    })

    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [setUser, onUserChangeSubscription])

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })

    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
    }
  }, [setConfig, onConfigChangeSubscription])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (user: User) => {
      setUser(user)
      if (!user?.squidId) {
        setRegisterCompleted(false)
        return
      }
      setRegisterCompleted((await hubCoreService.getRegisterCompleteness(user?.squidId))?.completed)
    })

    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [onUserChangeSubscription])

  const {
    isModalOpen,
    setIsModalOpen,
    handleButtonCreatorsInsightsClick,
    handleConfirm,
    isActivationModalOpen,
    setIsActivationModalOpen,
    handleToggleActivation,
    handleTogglePublicProfile,
    loadingToken,
  } = useCreatorsInsights({
    user,
    setProfiles,
  })

  const isMobile = useDeviceType()
  const handleCreatorsInsightsClick = async () => {
    const allProfiles = (await fetchAllProfiles()) ?? []

    handleButtonCreatorsInsightsClick(allProfiles)
  }

  const actionMenu = (setOpen: (value: SetStateAction<boolean>) => void) => {
    setOpen((open) => {
      open ? document.body.classList.remove('no-scroll') : document.body.classList.add('no-scroll')
      return !open
    })
  }

  const isCloseMobileMenuEvents = {
    onMouseEnter: () => setIsCloseMobileMenuHovered(true),
    onMouseLeave: () => setIsCloseMobileMenuHovered(false),
  }

  const [isMobileMenuButtonHoverd, setIsMobileMenuButtonHoverd] = useState(false)

  const mobileMenuButtonEvents = isMobile
    ? {}
    : {
        onMouseEnter: () => {
          setIsMobileMenuButtonHoverd(true)
        },
        onMouseLeave: () => {
          setIsMobileMenuButtonHoverd(false)
        },
      }

  return (
    <>
      {isModalOpen && (
        <SqModalWelcomeCreatorsInsights
          open={isModalOpen}
          modalSize="xl"
          buttonClose={true}
          onCloseChange={() => setIsModalOpen(false)}
          onOpenChange={(open: boolean) => setIsModalOpen(open)}
          forceMobileNoMargin={true}
          onConfirm={() => handleConfirm(profiles)}
          loading={loadingToken}
        />
      )}
      {isActivationModalOpen && (
        <SqModalActivation
          open={isActivationModalOpen}
          requireActiveProfile
          onConfirm={() => handleButtonCreatorsInsightsClick(profiles)}
          onToggleCreatorsInsights={handleToggleActivation}
          onTogglePublicProfile={handleTogglePublicProfile}
          loading={profilesLoading}
          profiles={
            profiles?.map((profile) => ({
              profileId: profile.profileId,
              socialNetwork: profile.socialNetwork,
              username: profile.username,
              hasCreatorsInsights: profile.hasCreatorsInsights ?? false,
              picture: profile.picture,
              followers: profile.followers,
              hasSocialNetworkCache: profile.hasSocialNetworkCache,
              isSharedCreatorsInsights: profile.isSharedCreatorsInsights ?? false,
              hasValidToken: profile.hasValidToken,
            })) ?? []
          }
          onOpenChange={(open: boolean) => setIsActivationModalOpen(open)}
          titleModal={t('modalActivation.activationQuestion')}
          messageModal={t('modalActivation.insightsText')}
          textButton={t('modalActivation.buttonDone')}
          registerCompleted={registerCompleted}
        />
      )}
      {mode === 'mobile' && (
        <button
          type="button"
          title="Menu"
          className={'mobile-menu-button'}
          {...(isMobile ? {} : mobileMenuButtonEvents)}
          onClick={() => actionMenu(setOpen)}
        >
          <i className={`fa-regular fa-bars ${isMobileMenuButtonHoverd ? 'hover' : ''}`} />
        </button>
      )}
      <div className={`wrapper-mobile-menu ${open ? 'open' : ''}`}>
        <div className="scrollable-content scrollbar">
          {mode === 'mobile' && (
            <div className="wrapper-mobile-menu-header">
              <span>{t('mainMenu.menu')}</span>
              <button className="close-button" onClick={() => actionMenu(setOpen)} {...(isMobile ? {} : isCloseMobileMenuEvents)}>
                <i className={`fa-regular icon-mobile fa-times ${isCloseMobileMenuHovered ? 'hover' : ''}`}></i>
              </button>
            </div>
          )}
          <div className="menu-mobile-item-section">
            <span>{t('mainMenu.main').toUpperCase()}</span>
          </div>
          <div className="menu-mobile-items-wrapper">
            <MainMenuItem link="/home" title={t('userMenu.home')} iconClasses="fa-house" setOpen={setOpen} mode={mode} />
            {showInsights({ configs, user }) && (
              <MainMenuItem
                title={t('userMenu.creatorsInsights')}
                iconClasses="fa-chart-user"
                onClick={handleCreatorsInsightsClick}
                setOpen={setOpen}
                mode={mode}
              />
            )}
            <MainMenuItem
              link="/profile/social-networks"
              title={t('userMenu.socialNetworks')}
              iconClasses="fa-link"
              setOpen={setOpen}
              mode={mode}
            />
            <MainMenuItem link="/mediakit" title={t('userMenu.mediakit')} iconClasses="fa-photo-film" setOpen={setOpen} mode={mode} />
          </div>

          <div className="menu-mobile-item-section increment-margin">
            <span>{t('userMenu.campaigns').toUpperCase()}</span>
          </div>
          <div className="menu-mobile-items-wrapper">
            <MainMenuItem link="/campaigns" title={t('campaignMenu.general.all')} iconClasses="fa-bullhorn" setOpen={setOpen} mode={mode} />
            <MainMenuItem
              link="/campaigns/participating"
              title={t('campaignMenu.mine.participations')}
              isSubItem
              setOpen={setOpen}
              mode={mode}
            />
            <MainMenuItem link="/campaigns/sent" title={t('campaignMenu.mine.inscriptions')} isSubItem setOpen={setOpen} mode={mode} />
            <MainMenuItem link="/campaigns/finished" title={t('campaignMenu.mine.finished')} isSubItem setOpen={setOpen} mode={mode} />
            <MainMenuItem link="/payments/info" title={t('mainMenu.pay')} iconClasses="fa-circle-dollar" setOpen={setOpen} mode={mode} />
          </div>
        </div>
      </div>
    </>
  )
}
